/**
 * ComingSoonPanel.tsx — the "not configured yet" panel for a clinic that
 * isn't live, shown on BOTH staff consoles (UX Wave 7B §5).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician +
 * admin); role-aware copy added in UX Wave 7B (2026-07-21 late).
 *
 * WHY: when `?clinic=` selects a clinic the roster marks `configured: false`
 * (see admin/clinics.ts), the whole section area is replaced by this centred,
 * friendly panel — no section renders at all for a non-live clinic. The copy
 * is ROLE-APPROPRIATE (Wave 7B §5): a CLINICIAN is simply reassured the
 * clinic is on its way; an ADMIN gets the launch-prep framing with a pointer
 * to Plan management (where a clinic's plans and pricing are configured once
 * its build lands). A small hand-rolled, decorative SVG (tokens-only fills,
 * aria-hidden) softens what would otherwise be a bare "nothing here" screen;
 * the one real action is a primary button back to the one clinic that IS
 * live. R28: category-level copy only — clinic names, never a POM name.
 */
import { useSearchParams } from 'react-router-dom';
import { Button, Help, Title } from '../ui/kit';
import { DEFAULT_CLINIC_SLUG, findClinic } from './clinics';

/** A simple decorative "under construction" glyph — tokens-only fills, purely illustrative. */
function ComingSoonIllustration() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="140"
      height="110"
      viewBox="0 0 140 110"
      style={{ margin: '0 auto var(--gps-s-4)', display: 'block' }}
    >
      <ellipse cx="70" cy="98" rx="46" ry="7" fill="var(--gps-primary-light)" />
      <rect x="30" y="26" width="80" height="58" rx="10" fill="var(--gps-surface)" stroke="var(--gps-border-strong)" strokeWidth="2" />
      <rect x="30" y="26" width="80" height="18" rx="10" fill="var(--gps-primary-light)" />
      <circle cx="42" cy="35" r="3" fill="var(--gps-cta)" />
      <circle cx="53" cy="35" r="3" fill="var(--gps-primary)" />
      <path d="M46 60 L60 74 L94 40" fill="none" stroke="var(--gps-primary)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.25" />
      <circle cx="70" cy="58" r="16" fill="none" stroke="var(--gps-primary)" strokeWidth="3" strokeDasharray="4 5" />
      <path d="M70 50 v8 l6 4" fill="none" stroke="var(--gps-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ComingSoonPanel({
  clinicName,
  audience = 'admin',
}: {
  clinicName: string;
  /** Which console is showing the panel — picks the role-appropriate copy (Wave 7B §5). */
  audience?: 'admin' | 'clinician';
}) {
  const [, setSearchParams] = useSearchParams();
  const liveClinicName = findClinic(DEFAULT_CLINIC_SLUG)?.name ?? 'Weight Loss Clinic';

  function goToWeightLoss() {
    setSearchParams({ clinic: DEFAULT_CLINIC_SLUG }, { replace: true });
  }

  return (
    <div style={{ textAlign: 'center', padding: 'var(--gps-s-6) var(--gps-s-4)' }}>
      <ComingSoonIllustration />
      <Title as="h2" size="22px">{clinicName} is coming soon</Title>
      {audience === 'clinician' ? (
        <Help>This clinic has not launched yet — it is planned for a future launch.</Help>
      ) : (
        <Help>
          We&rsquo;re preparing this clinic for launch — its sections unlock once it&rsquo;s configured.
          Its plans and pricing will be set up in Plan management when the build lands.
        </Help>
      )}
      <div style={{ maxWidth: '18rem', margin: '0 auto' }}>
        <Button type="button" variant="cta" onClick={goToWeightLoss}>
          Go to the {liveClinicName}
        </Button>
      </div>
    </div>
  );
}
